import "react-datepicker/dist/react-datepicker.css";
import { useCallback, useMemo, useState } from "react";
import { ReportApi } from "src/services/api/ReportApi";
import {
	ColDef,
	IServerSideDatasource,
	IServerSideGetRowsParams,
	LoadSuccessParams,
	GridApi,
	SizeColumnsToContentStrategy,
	ValueFormatterParams,
	ValueFormatterFunc
} from "ag-grid-enterprise";

import useSidebar from "src/hooks/useSidebar";
import useReport from "src/hooks/useReport";

import BaseGrid from "../../BaseGrid";
import LoadingComponent from "src/components/LoadingComponent";
import SearchCriteriaRedReports from "src/components/SearchCriteriaRedReports";
import { downloadFile } from "src/utils/DownloadFile";
import { formatCurrency } from "src/utils/FormatCurrency";
import useReference from "src/hooks/useReference";
import { CardAdjustmentModel } from "src/models/CardAdjustmentModel";

const CardAdjustment = () => {
	const { setIsOpen } = useSidebar();
	const { reportInputs, mapReportRequest, getContextMenuItems } = useReport();

	const [gridReady, setGridReady] = useState(false);
	const [gridApi, setGridApi] = useState<GridApi>();
	const [dataSource, setDataSource] = useState<IServerSideDatasource>();
	const [isDownload, setIsDownload] = useState(false);
	const [isLoading, setIsLoading] = useState(false);


    const { currencyList } = useReference();

    const numericToAlpha = useMemo<Record<string, string>>(() => {
        const map: Record<string, string> = {};
        (currencyList ?? []).forEach((c: any) => {
            if (c?.numericCode != null && c?.alphaCode) {
                map[String(c.numericCode)] = String(c.alphaCode).toUpperCase();
            }
        });
        return map;
    }, [currencyList]);

    const resolveAlphaFromRow = useCallback((data?: CardAdjustmentModel | null): string => {
        const feeVal = data?.currencyNumericCode;
        if (feeVal == null) return "USD";
        const str = String(feeVal).trim();
        if (/^[A-Za-z]{3}$/.test(str)) return str.toUpperCase();
        const mapped = numericToAlpha[String(str)];
        if (mapped) return mapped;
        return "USD";
    },[numericToAlpha]);


    const currencyFormatter = useCallback<ValueFormatterFunc<CardAdjustmentModel, any>>(
        (params: ValueFormatterParams<CardAdjustmentModel, any>): string => {
            const v = params?.value;
            if (v === null || v === undefined) return "";
            const n = typeof v === "number" ? v : Number(v);
            if (Number.isNaN(n)) return "";
            const alphaCode = resolveAlphaFromRow(params.data);
            return String(formatCurrency(n, alphaCode));
        },
        [resolveAlphaFromRow]
    );


    const colDefs = useMemo<ColDef<CardAdjustmentModel>[]>(() => [
        { headerName: "Transaction Date", field: "transactionDate" },
        { headerName: "Effective Date", field: "effectiveDate" },
        { headerName: "Card Number", field: "cardNumber" },
        { headerName: "Program Name", field: "program" },
        { headerName: "Merchant", field: "merchant" },
        { headerName: "Amount", field: "amount", valueFormatter: currencyFormatter },
        { headerName: "Actual Balance After Adjustment", field: "actualBalanceAfterAdjustment", valueFormatter: currencyFormatter },
        { headerName: "Available Balance After Adjustment", field: "availableBalanceAfterAdjustment", valueFormatter: currencyFormatter },
        { headerName: "Adjustment Type", field: "adjustmentType" },
        { headerName: "User Name", field: "contactName" },
        { headerName: "Note", field: "note" },
        { headerName: "EML Reason Code", field: "reason" },
        { headerName: "Adjusted System Transaction Id", field: "adjustedSystemTranID" },
        { headerName: "Portfolio", field: "portfolio" },
    ], [currencyFormatter]);

    const defaultColDef: ColDef = {
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
    };

    const autoSizeStrategy = useMemo<SizeColumnsToContentStrategy>(() => {
        return {
            type: "fitCellContents",
            defaultMinWidth: 120,
            defaultMaxWidth: 300,
            skipHeader: false,
        };
    }, []);

    const handleViewReport = () => {
        setIsDownload(false);
        setGridReady(true);
        setDataSource(createServerSideDatasource());
    };

    const handleDownloadReport = () => {
        setIsDownload(true);
        setIsLoading(true);
        ReportApi.exportCardAdjustment(mapReportRequest(null))
            .then((res: any) => {
                downloadFile(CardAdjustment.displayName!, res.data);
            })
            .finally(() => setIsLoading(false));
    };

    const createServerSideDatasource = (): IServerSideDatasource => {
        return {
            getRows: (params: IServerSideGetRowsParams) => {
                getReportData(params);
            },
        };
    };

    const getReportData = (params: IServerSideGetRowsParams | null) => {
        setIsOpen(false);

        ReportApi.getCardAdjustment(mapReportRequest(params))
            .then((res) => {
                const result = res.data;

                // Normalize date for grid display
                result?.rows?.forEach((x: CardAdjustmentModel) => {
                    x.transactionDate = x.transactionDate
                        ? new Date(x.transactionDate).toLocaleString()
                        : x.transactionDate;
                    x.effectiveDate = x.effectiveDate
                        ? new Date(x.effectiveDate).toLocaleDateString()
                        : x.effectiveDate;
                });

                // Push page rows into grid
                const success: LoadSuccessParams = {
                    rowData: result.rows,
                    rowCount: result.totalCount,
                };
                if (result?.rows?.length > 0) {
                    params?.api?.hideOverlay();
                    params?.success(success);
                } else {
                    params?.api?.showNoRowsOverlay();
                    params?.success({ rowData: [], rowCount: 0 });
                }
            
                // Add pinned bottom totals row if summary detail provided
                if (result.summaryDetail?.totalAmount) {
                    const summaryRows = result.summaryDetail.totalAmount?.map((item) => ({
                        transactionDate: "Totals",
                        amount: item.totalAmount,
                        actualBalanceAfterAdjustment: item.totalActualBalanceAfterAdjustment,
                        availableBalanceAfterAdjustment: item.totalAvailableBalanceAfterAdjustment,
                        currencyNumericCode: item.currencyNumericCode
                    }));

                    (params?.api as any).setGridOption('pinnedBottomRowData', summaryRows)
                } else {
                    (params?.api as any).setGridOption('pinnedBottomRowData', []);
                }
                
            })
            .catch(() => params?.fail?.());
    };


    return (
        <>
            <div className="report-container">
                <div className="report-header">
                    <h5 className="mb-0">Card Adjustment Report</h5>
                </div>
                <div className="blocks">
                    
                    <SearchCriteriaRedReports
                        handleViewReport={handleViewReport}
                        handleDownloadReport={handleDownloadReport}
                        useBin={true}
                        usePrograms={true}
                        showToDate={true}
                        useAdjustmentTypes={true}
                        useEmlReasonCodes={true}
                        usePortfolios={true}
                        useFirstLastName={true}
                    />
                </div>
                {isDownload && isLoading ? (
                    <LoadingComponent />
                ) : (
                    !isDownload && (
                        <BaseGrid
                            colDefs={colDefs}
                            defaultColDef={defaultColDef}
                            getData={getReportData}
                            isGridReady={gridReady}
                            reportInputs={reportInputs}
                            dataSource={dataSource}
                            gridApi={gridApi}
                            setGridApi={setGridApi}
                            getContextMenuItems={getContextMenuItems}
                            autoSizeStrategy={autoSizeStrategy}
                        />
                    )
                )}
            </div>
        </>
    );


};

CardAdjustment.displayName = "CardAdjustment";

export default CardAdjustment;